import React, { useState, useEffect } from "react";
import { Tooltip, Whisper } from "rsuite";
import styled from "styled-components";
import "rsuite/dist/rsuite.min.css";
import { DateTime } from "luxon";
import { SeverityAlert } from "../../../../appHelpers/appCommonSC";
import { SpacedGlassCard, Badge } from "../../../ui";

export const MagnetHeader = ({
  metadata,
  magnetData,
  dataConfig,
  alertConfigs,
}) => {
  const [recentValues, setRecentValues] = useState([]);

  const getMostRecentParameters = () => {
    const foundFields = {};
    const recentValue = [];

    // Loop through the data array
    magnetData?.dataTable.map((obj) => {
      for (const field in obj) {
        // Check if the field is null and hasn't been found before
        if (obj[field] !== null && !foundFields[field]) {
          // Store the field and datetime in the result array
          recentValue.push({
            field_name: field,
            field_value: obj[field],
            recent_datetime: obj.capture_datetime,
          });
          // Mark the field as found
          foundFields[field] = true;
        }
      }
    });
    setRecentValues(recentValue);
  };

  useEffect(() => {
    getMostRecentParameters();
  }, [magnetData]);

  const getVisibleParameters = () => {
    return (
      dataConfig[0]?.dataLabels?.filter((label) =>
        magnetData?.filterConfig?.some(
          (config) => config[2] === 1 && config[0] === label.pgColumn,
        ),
      ) || []
    );
  };

  const visibleParameters = getVisibleParameters();

  // Classify parameters as mag or env based on field naming patterns and chartgroup
  const classifyParameter = (label) => {
    const pgColumn = label.pgColumn?.toLowerCase() || "";
    const chartGroup = label.chartgroup;

    // Environmental parameters - typically room monitoring or external probes
    const envPatterns = [
      "room_temp",
      "room_humidity",
      "temp_probe",
      "ambient",
      "comp_vib_status",
      "x_axis",
      "y_axis",
      "z_axis",
      "axis_sum",
    ];

    // Check if it matches environmental patterns
    if (envPatterns.some((pattern) => pgColumn.includes(pattern))) {
      return "env";
    }

    // Magnet-related parameters - everything else (helium, pressure, sensors, etc.)
    return "mag";
  };

  // Group parameters by type
  const groupedParameters = visibleParameters.reduce((acc, label) => {
    const type = classifyParameter(label);
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(label);
    return acc;
  }, {});

  const renderParameterCard = (label) => {
    const recentValue = recentValues?.find(
      (value) => value.field_name === label.pgColumn,
    );
    const hasUserAlerts =
      alertConfigs?.filter(
        (config) =>
          config.field_name === label?.pgColumn && config.user_id !== "default",
      ).length > 0;

    return (
      <Whisper
        key={label.pgColumn}
        trigger="hover"
        speaker={
          <Tooltip>
            {recentValue
              ? `Last Read: ${DateTime.fromISO(recentValue.recent_datetime).toFormat("ff")}`
              : "No recent data"}
          </Tooltip>
        }
      >
        <ParameterCard>
          <ParameterLabel>{label.uiLabel}</ParameterLabel>

          <ParameterValue>
            {recentValue ? (
              hasUserAlerts ? (
                renderAlertValue(
                  label,
                  recentValue,
                  alertConfigs,
                  magnetData,
                  metadata,
                )
              ) : (
                renderNormalValue(label, recentValue, magnetData, metadata)
              )
            ) : (
              <NoDataBadge variant="neutral">No Data</NoDataBadge>
            )}
          </ParameterValue>
        </ParameterCard>
      </Whisper>
    );
  };

  return (
    <MagnetHeaderContainer>
      <SpacedGlassCard>
        <HeaderContent>
          {/* Magnet Parameters Section */}
          {groupedParameters.mag && groupedParameters.mag.length > 0 && (
            <ParameterSection>
              <SectionLabel>Magnet Data</SectionLabel>
              <ParametersGrid columns={groupedParameters.mag.length}>
                {groupedParameters.mag.map(renderParameterCard)}
              </ParametersGrid>
            </ParameterSection>
          )}

          {/* Environmental Parameters Section */}
          {groupedParameters.env && groupedParameters.env.length > 0 && (
            <ParameterSection>
              <SectionLabel>Environmental Data</SectionLabel>
              <ParametersGrid columns={groupedParameters.env.length}>
                {groupedParameters.env.map(renderParameterCard)}
              </ParametersGrid>
            </ParameterSection>
          )}
        </HeaderContent>
      </SpacedGlassCard>
    </MagnetHeaderContainer>
  );
};

const renderAlertValue = (
  label,
  recentValue,
  alertConfigs,
  magnetData,
  metadata,
) => {
  if (label?.pgColumn === "comp_vib_status") {
    if (recentValue.field_value === null)
      return <NoDataBadge variant="neutral">-</NoDataBadge>;
    return recentValue.field_value === 1 ? (
      <SeverityAlert>ON</SeverityAlert>
    ) : (
      <SeverityAlert severity="high">OFF</SeverityAlert>
    );
  }

  if (
    (label?.pgColumn === "he_level_1_value" ||
      label?.pgColumn === "he_level_value") &&
    metadata.he_tank_volume &&
    magnetData?.chartConfig
      ?.find((config) => config.pgcolumn === label.pgColumn)
      ?.unit.toUpperCase() === "LTRS"
  ) {
    if (recentValue.field_value === null)
      return <NoDataBadge variant="neutral">-</NoDataBadge>;

    return displayHeliumSeverityAlert(
      alertConfigs.filter(
        (config) =>
          config.field_name === label.pgColumn && config.user_id !== "default",
      ),
      recentValue.field_value,
      magnetData?.chartConfig,
      metadata.he_tank_volume,
    );
  }

  if (recentValue.field_value === null)
    return <NoDataBadge variant="neutral">-</NoDataBadge>;

  return displaySeverityAlert(
    alertConfigs.filter(
      (config) =>
        config.field_name === label.pgColumn && config.user_id !== "default",
    ),
    recentValue.field_value,
    magnetData?.chartConfig,
  );
};

const renderNormalValue = (label, recentValue, magnetData, metadata) => {
  if (recentValue.field_value === null)
    return <NoDataBadge variant="neutral">-</NoDataBadge>;

  const unit =
    magnetData?.chartConfig?.find(
      (config) => config.pgcolumn === label.pgColumn,
    )?.unit || "";

  // Handle helium percentage conversion
  if (
    (label?.pgColumn === "he_level_1_value" ||
      label?.pgColumn === "he_level_value") &&
    metadata.he_tank_volume &&
    unit.toUpperCase() === "LTRS"
  ) {
    const percentage = (
      (parseFloat(recentValue.field_value) /
        parseFloat(metadata.he_tank_volume)) *
      100
    ).toFixed(2);
    return <ValueText>{percentage} %</ValueText>;
  }

  return (
    <ValueText>
      {recentValue.field_value} {unit}
    </ValueText>
  );
};

const displaySeverityAlert = (configs, rowValue, chartConfig) => {
  let mediumAlert = null;
  let unit = null;

  for (let config of configs) {
    if (
      ((config.operator === "less_than" &&
        parseFloat(rowValue) < parseFloat(config.threshold)) ||
        (config.operator === "greater_than" &&
          parseFloat(rowValue) > parseFloat(config.threshold)) ||
        (config.operator === "equals" &&
          parseFloat(rowValue) === parseFloat(config.threshold))) &&
      rowValue !== null
    ) {
      if (config.severity === "high") {
        return (
          <SeverityAlert severity="high">
            {rowValue}{" "}
            {
              chartConfig?.filter(
                (configUnit) => configUnit?.pgcolumn === config.field_name,
              )?.[0]?.unit
            }
          </SeverityAlert>
        );
      } else if (config.severity === "medium") {
        mediumAlert = (
          <SeverityAlert severity="medium">
            {rowValue}{" "}
            {
              chartConfig?.filter(
                (configUnit) => configUnit?.pgcolumn === config.field_name,
              )?.[0]?.unit
            }
          </SeverityAlert>
        );
      }
    }
    unit = chartConfig?.filter(
      (configUnit) => configUnit?.pgcolumn === config.field_name,
    )?.[0]?.unit;
  }

  if (mediumAlert) {
    return mediumAlert;
  } else {
    return (
      <SeverityAlert>
        {rowValue} {unit}
      </SeverityAlert>
    );
  }
};

const displayHeliumSeverityAlert = (
  configs,
  rowValue,
  chartConfig,
  tankVolume,
) => {
  let mediumAlert = null;
  let unit = null;

  for (let config of configs) {
    if (
      (config.operator === "less_than" &&
        ((parseFloat(rowValue) / parseFloat(tankVolume)) * 100).toFixed(2) <
          parseFloat(config.threshold)) ||
      (config.operator === "greater_than" &&
        ((parseFloat(rowValue) / parseFloat(tankVolume)) * 100).toFixed(2) >
          parseFloat(config.threshold)) ||
      (config.operator === "equals" &&
        parseFloat(rowValue) === parseFloat(config.threshold))
    ) {
      if (config.severity === "high") {
        return (
          <SeverityAlert severity="high">
            {((parseFloat(rowValue) / parseFloat(tankVolume)) * 100).toFixed(2)}{" "}
            %{" "}
          </SeverityAlert>
        );
      } else if (config.severity === "medium") {
        mediumAlert = (
          <SeverityAlert severity="medium">
            {((parseFloat(rowValue) / parseFloat(tankVolume)) * 100).toFixed(2)}{" "}
            %{" "}
          </SeverityAlert>
        );
      }
    }
    unit = chartConfig?.filter(
      (configUnit) => configUnit.pgcolumn === config.field_name,
    )?.[0]?.unit;
  }

  if (mediumAlert) {
    return mediumAlert;
  } else {
    return (
      <SeverityAlert>
        {((parseFloat(rowValue) / parseFloat(tankVolume)) * 100).toFixed(2)} %
      </SeverityAlert>
    );
  }
};

// Styled Components
const MagnetHeaderContainer = styled.div`
  width: 70%;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 80%;
  }
`;

const HeaderContent = styled.div`
  padding: 1rem;
`;

const ParameterSection = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
  text-align: center;
  position: relative;
  padding-bottom: 0.5rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 70%;
    height: 2px;
    background: rgba(44, 90, 160, 0.6);
    border-radius: 1px;
  }
`;

const ParametersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, max-content));
  gap: 0.8rem;
  justify-content: center;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(130px, max-content));
    gap: 0.7rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(120px, max-content));
    gap: 0.6rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fit, minmax(110px, max-content));
    gap: 0.5rem;
  }
`;

const ParameterCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: auto;
  max-width: 130px;
  margin: 0 auto;
  gap: 2px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 42px;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }

  @media (max-width: 768px) {
    max-width: 110px;
    padding: 3px 6px;
    min-height: 38px;
  }
`;

const ParameterLabel = styled.div`
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  text-align: center;
  text-transform: uppercase;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const ParameterValue = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  min-height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ValueText = styled.span`
  color: rgba(0, 0, 0, 0.9);
  font-size: 0.8rem;
`;

const NoDataBadge = styled(Badge)`
  font-size: 0.65rem;
  padding: 0.2rem 0.4rem;
`;
